import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ArrowCircleDown,
  CancelOutlined,
  Send,
  Visibility,
} from '@mui/icons-material';
import { FAILED_LIST_BUTTON } from './const';
import { publicHeadCells } from '../GSToDoList';
import { GS_FILTER_API_TYPE, GSTableFilter } from '../GSTableFilter';
import { MONITOR_LIST_TEXT } from '../../../lang';
import {
  useReleaseFailedResult,
  useRevokeFailedResult,
  useGetFailedList,
  type FailedColumnData,
} from '../../../graphql';
import {
  Alert,
  Loading,
  IconButton,
  pageRowsSelectOptions,
  AdvancedTable,
  type HeadCell,
  DeepMerge,
  type AlertDetails,
} from '../../../../../components';
import { OneOffDialog, type OneOffDialogConfig } from '../../../components';

type FailedListPageProps = {
  activateButton: FAILED_LIST_BUTTON;
  filter?: any;
  children?: React.ReactNode;
};

export const FailedListPage: React.FC<FailedListPageProps> = props => {
  const { activateButton, filter: initialFilter = {}, children } = props;

  const [page, setPage] = useState(1); // default current page is 1
  const [selectRowsLimit, setSelectRowsLimit] = useState(
    pageRowsSelectOptions[0].value as number, // default rows per page is the first option
  );
  const [filter, setFilter] = useState(initialFilter);

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [oneOffDialogConfig, setOpenOneOffDialog] =
    useState<OneOffDialogConfig>({
      open: false,
      applicationId: '',
      applicationNo: '',
    });
  const [appliedFilter, setAppliedFilter] = useState<any>(null); // set for table filter

  const {
    failedListData,
    loading,
    error: listError,
    refetch,
  } = useGetFailedList(filter ?? {}, {}, { page, limit: selectRowsLimit });

  const visibleData = failedListData?.failedList;
  const pageCount = failedListData?.totalPage;
  const recordsCount = failedListData?.count;

  const headCells: Array<HeadCell<FailedColumnData>> = [
    ...publicHeadCells,
    {
      id: 'action',
      label: MONITOR_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: FailedColumnData) => action(row),
    },
  ];

  const releaseResult = useReleaseFailedResult();
  const revokeResult = useRevokeFailedResult();

  const handleAnnounceResult = async (applicationId: string): Promise<void> => {
    try {
      await releaseResult({
        variables: {
          input: {
            applicationIds: [applicationId],
          },
        },
        fetchPolicy: 'network-only',
      });
      setAlertDetails({
        alertContent: 'Announced the Result Successfully.',
        alertType: 'success',
      });
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'warning',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  const handleRevokeResult = async (applicationId: string): Promise<void> => {
    try {
      await revokeResult({
        variables: {
          input: {
            applicationIds: [applicationId],
          },
        },
        fetchPolicy: 'network-only',
      });
      setAlertDetails({
        alertContent: 'Revoked the Result Successfully.',
        alertType: 'success',
      });
    } catch (error) {
      setAlertDetails({
        alertContent: 'Error Occurs.' + (error as Error)?.message,
        alertType: 'warning',
      });
    } finally {
      setOpenAlert(true);
    }
  };

  const action = (row: FailedColumnData): React.ReactNode => {
    const { applicationId, applicationNo } = row;
    const ViewMonitorButton: React.ReactNode = (
      <IconButton
        onClick={handleViewApplication(applicationId)}
        color="primary"
        icon={<Visibility />}
      />
    );

    const AnnounceResultButton: React.ReactNode = (
      <IconButton
        onClick={async () => {
          await handleAnnounceResult(applicationId);
          refetch();
        }}
        title="Announce the result"
        color="primary"
        icon={<Send />}
      />
    );

    const RevokeResultButton: React.ReactNode = (
      <IconButton
        onClick={async () => {
          await handleRevokeResult(applicationId);
          refetch();
        }}
        color="primary"
        title="Revoke the result"
        icon={<CancelOutlined />}
      />
    );

    const DownLoadOneoff: React.ReactNode = (
      <IconButton
        onClick={() => {
          setOpenOneOffDialog({
            open: true,
            applicationId,
            applicationNo,
          });
        }}
        title="Download Profile"
        color="primary"
        icon={<ArrowCircleDown />}
      />
    );

    const activateButtonActionMap: Record<
      FAILED_LIST_BUTTON,
      React.ReactNode[]
    > = {
      [FAILED_LIST_BUTTON.ALL]: [],
      [FAILED_LIST_BUTTON.READY_TO_RELEASE]: [AnnounceResultButton],
      [FAILED_LIST_BUTTON.RELEASING]: [RevokeResultButton],
      [FAILED_LIST_BUTTON.REVOKED]: [AnnounceResultButton],
      [FAILED_LIST_BUTTON.RELEASED]: [],
    };

    return (
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-2">
          {[
            ...(activateButtonActionMap[activateButton] ?? []),
            ViewMonitorButton,
            DownLoadOneoff,
          ].map((button, index) => {
            return <span key={index}>{button}</span>;
          })}
        </div>
      </div>
    );
  };

  const handleViewApplication =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) window.open(`/internal/monitor/${applicationId}`);
    };

  const handleRequestSort = (property: keyof FailedColumnData): void => {
    // TODO: Get the sorted data from the server
  };

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSelectRowsLimit(parseInt(event.target.value));
    setPage(1);
  };

  if (listError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (listError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }
  const { open: openDialog } = oneOffDialogConfig;

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {openDialog && (
        <OneOffDialog
          openDialogConfig={oneOffDialogConfig}
          onDialogClose={() => {
            setOpenOneOffDialog({
              open: false,
              applicationId: '',
              applicationNo: '',
            });
          }}
        />
      )}

      <GSTableFilter
        apiType={GS_FILTER_API_TYPE.FailedApplicationListFilter}
        initialAppliedFilter={appliedFilter}
        setFilter={(filter: any, appliedFilter: any) => {
          setPage(1);
          setFilter(DeepMerge(filter, initialFilter));
          setAppliedFilter(appliedFilter);
        }}
      />

      {loading ? (
        <Loading />
      ) : (
        <Container>
          <AdvancedTable<FailedColumnData>
            extendFunctionAreas={children}
            tableProps={{
              headCells,
              data: visibleData,
              handleRequestSort,
            }}
            advancedPaginationProps={{
              page,
              count: pageCount,
              recordText: `${recordsCount} records`,
              rowsPerPageOptions: pageRowsSelectOptions,
              rowsPerPage: selectRowsLimit,
              onChangeRowsPerPage: handleChangeRowsPerPage,
            }}
            handleChangePage={handleChangePage}
          />
        </Container>
      )}
    </div>
  );
};

const Container = styled.div`
  h1 {
    text-align: center;
    font-size: 2rem;
    margin: 2rem 0;
  }
`;
